/*
 * Ankh — canonical brand mark for Cleo's Secret.
 *
 * Hand-drawn proportions based on Late Ptolemaic / Cleopatra-era ankhs:
 * - Dominant loop (~49% of total height) — not a Christian-cross small head
 * - Slim-centre, wide-tip crossbar with rounded ends
 * - Thick tapered shaft (slim at top, wider at bottom) with rounded foot
 * - Loop and crossbar drawn as ONE continuous outer path so they blend seamlessly
 *
 * This is the SAME implementation embedded inline in LandingPage.jsx.
 * Both must stay in sync. See docs/design-spec.md Section 9.1 for the full spec.
 *
 * Coordinate system: native viewBox -16 0 80 174 (width 80, height 174).
 * The central axis sits at x=24. The loop spans y=0 to y=82.
 * The crossbar spans y=82 to y=118. The shaft spans y=118 to y=174.
 *
 * Usage — two modes:
 *
 *   1. Responsive (preferred for all spec'd contexts):
 *      <Ankh responsive="opener" />                   // 96px desktop / 60px mobile, GO, 1.0
 *      <Ankh responsive="closing" />                  // 80px desktop / 52px mobile, GO, 1.0
 *      <Ankh responsive="footer" color={GO2} />       // 42px desktop / 32px mobile, GO2, 0.85
 *      <Ankh responsive="tierFlank" />                // 28px desktop / 22px mobile, GO, 0.92
 *      <Ankh responsive="tierCrown" />                // 36px desktop / 28px mobile, GO, 1.0
 *      <Ankh responsive="clamp(40px, 4vw, 56px)" />   // ad-hoc clamp string
 *
 *   2. Fixed pixel (legacy / decorative / non-spec contexts):
 *      <Ankh size={36} />                             // 36px tall, GO, 0.9 opacity
 *      <Ankh size={48} color={P.GO} opacity={0.55} /> // mid-page decoration
 *
 * Responsive mode renders the SVG with CSS clamp() on style.height and
 * width: 'auto' — the viewBox aspect ratio takes over and the SVG scales
 * with the viewport. Pure CSS, SSR-safe, no JS measurement.
 *
 * Preset values match spec §9.1's "Responsive sizing" table.
 */

import React from 'react';

/* Loop and crossbar — one continuous outer path with the loop hole as a sub-path.
   The loop blends seamlessly into the crossbar with no visible seam. */
const ANKH_LOOP_CROSSBAR = "M 24 0 C 10 0 0 13 0 28 C 0 46 8 64 18 76 C 24 84 28 90 30 96 Q 38 95 46 92 Q 54 89 60 86 Q 64 88 64 92 L 64 108 Q 64 112 60 114 Q 54 111 46 108 Q 38 105 30 104 L 30 118 L 18 118 L 18 104 Q 10 105 2 108 Q -6 111 -12 114 Q -16 112 -16 108 L -16 92 Q -16 88 -12 86 Q -6 89 2 92 Q 10 95 18 96 C 20 90 24 84 30 76 C 40 64 48 46 48 28 C 48 13 38 0 24 0 Z M 24 10 C 15 10 9 20 9 30 C 9 46 18 64 24 68 C 30 64 39 46 39 30 C 39 20 33 10 24 10 Z";

/* Shaft — thick, tapered slim-to-wide, with rounded foot. */
const ANKH_SHAFT = "M 18 118 Q 16 134 14 150 Q 12 162 10 170 Q 12 174 16 174 L 32 174 Q 36 174 38 170 Q 36 162 34 150 Q 32 134 30 118 Z";

/* Responsive presets — match spec §9.1 "Responsive sizing" table.
   Each preset's clamp() controls SVG height; width derives from viewBox aspect. */
const RESPONSIVE_PRESETS = {
  tierFlank: { clamp: 'clamp(22px, 4vw, 28px)', defaultOpacity: 0.92 },
  tierCrown: { clamp: 'clamp(28px, 5vw, 36px)', defaultOpacity: 1.0  },
  footer:    { clamp: 'clamp(32px, 6vw, 42px)', defaultOpacity: 0.85 },
  closing:   { clamp: 'clamp(52px, 5vw, 80px)', defaultOpacity: 1.0  },
  opener:    { clamp: 'clamp(60px, 7vw, 96px)', defaultOpacity: 1.0  },
};

export default function Ankh({ size = 36, color = '#C9A961', opacity, responsive }) {
  if (responsive) {
    const preset = typeof responsive === 'string' ? RESPONSIVE_PRESETS[responsive] : null;
    const clampValue = preset ? preset.clamp : responsive;
    const finalOpacity = opacity != null ? opacity : (preset ? preset.defaultOpacity : 0.9);
    return (
      <svg
        viewBox="-16 0 80 174"
        style={{ display: 'block', height: clampValue, width: 'auto', opacity: finalOpacity }}
        aria-hidden="true"
      >
        <path d={ANKH_LOOP_CROSSBAR} fill={color} fillRule="evenodd" />
        <path d={ANKH_SHAFT} fill={color} />
      </svg>
    );
  }
  return (
    <svg
      width={Math.round(size * 80 / 174)}
      height={size}
      viewBox="-16 0 80 174"
      style={{ display: 'block', opacity: opacity != null ? opacity : 0.9 }}
      aria-hidden="true"
    >
      <path d={ANKH_LOOP_CROSSBAR} fill={color} fillRule="evenodd" />
      <path d={ANKH_SHAFT} fill={color} />
    </svg>
  );
}
